/**
 * Utility functions for formatting dates, numbers, and other data types
 */

/**
 * Format a date string to a short, readable format
 * @param dateString - ISO date string or any valid date format
 * @returns Formatted date string (e.g., "Jan 15, 2024") or "N/A" if invalid
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format a date string to include time
 * @param dateString - ISO date string or any valid date format
 * @returns Formatted datetime string (e.g., "Jan 15, 2024, 3:30 PM") or "N/A" if invalid
 */
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'N/A';
  }
};

/**
 * Format bytes to human-readable file size
 * @param bytes - File size in bytes
 * @returns Formatted file size string (e.g., "1.5 MB", "250 KB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Format a number as US currency
 * @param amount - Numeric amount
 * @returns Formatted currency string (e.g., "$1,234.56")
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

